import { Component, EventEmitter, Inject, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { L10nLocale, L10N_LOCALE, L10nTranslationService } from 'angular-l10n';
import { Metadata } from 'src/app/hickory-api/model/metadata';
import { AuthService } from '../authentication/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { AdminsApi, GroupsApi, SettingsApi } from 'src/app/hickory-api';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { AuthorizationViewModel, GenericAuthzCollection } from './shared/shared-authz-models';
import { GroupAuthZCollection, RoleAuthZCollection, PeopleAuthZCollection, EveryoneAuthZCollection, OwnerAuthZCollection } from './shared/authz-collections';
import { AuthZAccessControl, AuthZDomain } from '../authentication/access-control';

@Component({
  selector: 'app-authorization',
  templateUrl: './authorization.component.html',
  styleUrl: './authorization.component.scss',
  standalone: false
})
export class AuthorizationComponent implements OnInit, OnChanges {
  @Input() useInlineTitle = false;
  @Input() domain: AuthZDomain;
  @Input() metadata: Metadata;
  @Input() defaultAction: string = 'read';
  @Input() actions: string[] = ['read', 'update', 'delete'];
  @Input() isReadOnlyMode = false;
  @Output() metadataChange: EventEmitter<Metadata> = new EventEmitter<Metadata>();

  everyoneAuthorization: EveryoneAuthZCollection;
  authorizedOwner: OwnerAuthZCollection;
  authorizedGroups: GroupAuthZCollection;
  authorizedRoles: RoleAuthZCollection;
  authorizedPeople: PeopleAuthZCollection;

  get metadataJson(): string { return JSON.stringify(this.metadata, null, 4); }
  get isEveryoneDefault(): boolean { return AuthZAccessControl.canAccessAuthZType(this.authService, 'everyone-default'); }
  get hasCustomAuthorization(): boolean { return AuthZAccessControl.canAccessAuthZType(this.authService, 'custom'); }
  get canManageAccess(): boolean { return AuthZAccessControl.canAccessAuthZDomain(this.authService, this.domain); }

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private readonly translation: L10nTranslationService,
    private readonly dialog: MatDialog,
    private readonly authService: AuthService,
    private readonly groupsApi: GroupsApi,
    private readonly settingsApi: SettingsApi,
    private readonly adminsApi: AdminsApi,
  ) { }

  private initializeCollections(): void {
    this.everyoneAuthorization = new EveryoneAuthZCollection(this.authService, this.metadata, this.actions);
    this.authorizedOwner = new OwnerAuthZCollection(this.domain, this.authService, this.adminsApi, this.metadata, this.dialog, this.actions);
    this.authorizedPeople = new PeopleAuthZCollection(this.authService, this.adminsApi, this.metadata, this.dialog, this.actions);
    this.authorizedGroups = new GroupAuthZCollection(this.authService, this.groupsApi, this.metadata, this.dialog, this.actions);
    this.authorizedRoles = new RoleAuthZCollection(this.authService, this.settingsApi, this.metadata, this.dialog, this.actions);

    this.authorizedOwner.loadData();
    this.authorizedPeople.loadData();
    this.authorizedGroups.loadData();
    this.authorizedRoles.loadData();
  }

  ngOnInit(): void {
    this.initializeCollections();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.metadata && !changes.metadata.firstChange) {
      this.initializeCollections();
    }
  }

  onAdd(collection: RoleAuthZCollection | GroupAuthZCollection | PeopleAuthZCollection  | OwnerAuthZCollection) {
    collection.addItems(this.metadata)
      .subscribe(metadata => this.onUpdateMetadata(metadata));
  }

  onToggleModelAction(action: string, model: AuthorizationViewModel, collection: GenericAuthzCollection, event: MatCheckboxChange) {
    if (collection.type === this.everyoneAuthorization.type) {
      this.onUpdateMetadata(this.everyoneAuthorization.toggleEveryoneActionAndGetMetadata(
        model, action, event.checked, this.metadata,
        this.authorizedPeople, this.authorizedGroups, this.authorizedRoles
      ));
    } else {
      this.onUpdateMetadata(collection
        .toggleItemAction(model, action)
        .getUpdatedMetadata(this.metadata));
    }
  }

  onDeleteModel(model: AuthorizationViewModel, collection: GenericAuthzCollection) {
    this.onUpdateMetadata(collection
      .removeItem(model)
      .getUpdatedMetadata(this.metadata));
  }

  onUpdateMetadata(updates: Metadata) {
    this.removeEmptyTags(updates);
    this.metadata = updates;
    this.metadataChange.emit(updates);
  }

  removeEmptyTags(updates: Metadata) {
    for (const tag in updates.tags) {
      if (!this.metadata.tags[tag]?.length) {
        delete this.metadata.tags[tag];
      }
    }
  }

  getDataTestAttr(...params: string[]) {
    return params.join('_');
  }

  getActionTip(collectionName: string, action: string) {
    return this.getTranslatedTip('AuthorizationComponent.ActionCheckboxTip', { collectionName, action });
  }

  getAddButtonTip(collectionName: string) {
    return this.getTranslatedTip('AuthorizationComponent.AddItemTip', { collectionName });
  }

  getRemoveButtonTip(collectionName: string, itemName: string) {
    itemName = itemName || 'item';
    return this.getTranslatedTip('AuthorizationComponent.RemoveItemTip', { collectionName, itemName });
  }
 
  getTranslatedTip(message: string, params: { [key: string]: string }) {
    return this.translation.translate(message, params, this.locale.language);
  }
}